import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "video_url": "https://example.com/dance_video.mp4"
 *   }
 * }
 * ```
 */
export interface AlibabaAnimateAnyoneTemplateParams {
  /**
   * 输入参数
   */
  input: {
    /**
     * 视频URL，必须是有效的HTTP或HTTPS链接，支持mp4/avi/mov格式，最大200MB，系统会自动处理并上传
     */
    video_url: string;
  };
}

export interface AlibabaAnimateAnyoneTemplateResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_animate_anyone_template
 *
 * Alibaba AnimateAnyone 动作模板生成接口，从包含人物运动的视频中提取动作特征，生成可复用的动作模板。
 * 
 * Model: animate-anyone-template-gen2
 * Features:
 * - 从视频中提取人物动作特征
 * - 生成标准化动作模板ID
 * - 支持多种视频格式（mp4/avi/mov）
 * - 自动处理和上传视频
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/animate-anyone-template-api
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaAnimateAnyoneTemplate } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaAnimateAnyoneTemplate(
 *   {
 *     "input": {
 *       "video_url": "https://example.com/dance_video.mp4"
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaAnimateAnyoneTemplateParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaAnimateAnyoneTemplate(
  params: AlibabaAnimateAnyoneTemplateParams,
): Promise<AlibabaAnimateAnyoneTemplateResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_animate_anyone_template',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaAnimateAnyoneTemplateResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
